import React, { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function EducationSection({ profile }) {
  const queryClient = useQueryClient();
  const educationList = profile.education || [];

  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    degree: "",
    institution: "",
    start_year: "",
    end_year: "",
    course_type: "Full Time",
    grade: "",
  });

  // MUTATIONS
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editId) {
        return backendApi.entities.UserProfile.updateEducation(editId, data);
      }
      return backendApi.entities.UserProfile.addEducation(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.UserProfile.deleteEducation(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  const resetForm = () => {
    setEditId(null);
    setFormData({
      degree: "",
      institution: "",
      start_year: "",
      end_year: "",
      course_type: "Full Time",
      grade: "",
    });
  };

  const openAdd = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEdit = (edu) => {
    setEditId(edu.id);
    setFormData(edu);
    setIsOpen(true);
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this education record?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Education</h3>

        <button
          onClick={openAdd}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {(profile?.education && profile.education.length > 0) ? "Edit" : "Add"}
        </button>
      </div>

      {/* List */}
      <div className="space-y-4">
        {educationList.length > 0 ? (
          educationList.map((edu) => (
            <div
              key={edu.id}
              className="border-b border-gray-100 pb-4 last:border-0 relative group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    {edu.degree}
                    <button
                      onClick={() => openEdit(edu)}
                      className="text-gray-400 hover:text-blue-600"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(edu.id)}
                      className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </h4>
                  <p className="text-gray-600">{edu.institution}</p>

                  <p className="text-sm text-gray-500">
                    {edu.start_year && `${edu.start_year} - `}
                    {edu.end_year}
                    {edu.course_type && ` | ${edu.course_type}`}
                    {edu.grade && ` | Grade: ${edu.grade}`}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500">Add your education details</p>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editId ? "Edit Education" : "Add Education"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Degree */}
            <div>
              <Label>Degree / Qualification</Label>
              <Input
                value={formData.degree}
                onChange={(e) =>
                  setFormData({ ...formData, degree: e.target.value })
                }
                placeholder="e.g. B.Tech, MBA, Class XII"
              />
            </div>

            {/* Institution */}
            <div>
              <Label>Institution / University / Board</Label>
              <Input
                value={formData.institution}
                onChange={(e) =>
                  setFormData({ ...formData, institution: e.target.value })
                }
                placeholder="e.g. Delhi University"
              />
            </div>

            {/* Years */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Year</Label>
                <Input
                  type="number"
                  value={formData.start_year}
                  onChange={(e) =>
                    setFormData({ ...formData, start_year: e.target.value })
                  }
                  placeholder="2018"
                />
              </div>

              <div>
                <Label>End Year</Label>
                <Input
                  type="number"
                  value={formData.end_year}
                  onChange={(e) =>
                    setFormData({ ...formData, end_year: e.target.value })
                  }
                  placeholder="2022"
                />
              </div>
            </div>

            {/* Grade */}
            <div>
              <Label>Grade / CGPA</Label>
              <Input
                value={formData.grade}
                onChange={(e) =>
                  setFormData({ ...formData, grade: e.target.value })
                }
                placeholder="e.g. 8.5 CGPA or 85%"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
